import { useNodeViewContext, useNodeViewFactory } from '@prosemirror-adapter/react';
import { $node, $view } from '@milkdown/kit/utils';
import type { MilkdownPlugin } from '@milkdown/kit/ctx';

import React, { useCallback, useMemo } from 'react';

import { decodeBlockDataAs, encodeBlockData } from '../core/data-encoding';
import type { BlockPlugin } from '../core/types';

/** Shape of a remark-directive leaf directive mdast node */
interface DirectiveMdastNode {
    type: string;
    name?: string;
    attributes?: Record<string, string>;
}

/**
 * Bridge component that adapts ProseMirror node view context
 * to BlockViewProps for a given BlockPlugin's Component.
 */
function DirectiveNodeView<TData>({
    plugin
}: {
    plugin: BlockPlugin<TData>;
}) {
    const { node, setAttrs, view, getPos } = useNodeViewContext();
    const readOnly = !view.editable;
    const nodeId = `milkdown-${plugin.name}-${node.attrs.__id ?? ''}`;

    const data = useMemo<TData>(() => {
        if (!plugin.hasData) {
            const def = typeof plugin.defaultData === 'function'
                ? (plugin.defaultData as () => TData)()
                : (plugin.defaultData ?? ({} as TData));
            return def;
        }

        const encoded = node.attrs.data as string | undefined;
        const fallback = typeof plugin.defaultData === 'function'
            ? (plugin.defaultData as () => TData)()
            : (plugin.defaultData ?? ({} as TData));

        return decodeBlockDataAs<TData>(encoded, fallback);
    }, [node.attrs.data, plugin]);

    const onDataChange = useCallback(
        (newData: TData) => {
            if (!plugin.hasData) return;
            const encoded = encodeBlockData(newData);
            setAttrs({ data: encoded });
        },
        [plugin.hasData, setAttrs]
    );

    const onDelete = useCallback(() => {
        const pos = getPos();
        if (pos == null) return;
        const tr = view.state.tr.delete(pos, pos + node.nodeSize);
        view.dispatch(tr);
    }, [view, node.nodeSize, getPos]);

    return React.createElement(plugin.Component, {
        data,
        onDataChange,
        onDelete,
        readOnly,
        nodeId
    });
}

/**
 * Creates a ProseMirror $node and $view pair for a BlockPlugin.
 *
 * The $node handles:
 *   - Parsing ::directiveName{data="base64..."} from markdown (via remark-directive)
 *   - Serialising back to ::directiveName{data="base64..."} on save
 *
 * The $view renders the plugin's React Component with BlockViewProps.
 */
export function createDirectivePlugins(
    plugin: BlockPlugin,
    nodeViewFactory: ReturnType<typeof useNodeViewFactory>
): MilkdownPlugin[] {
    const nodePlugin = $node(plugin.name, () => ({
        group: 'block',
        atom: true,
        isolating: true,
        marks: '',
        defining: true,
        attrs: {
            ...(plugin.hasData ? { data: { default: null } } : {}),
            __id: { default: null }
        },
        parseDOM: [
            {
                tag: `div[data-directive="${plugin.name}"]`,
                getAttrs: (dom) => {
                    return plugin.hasData
                        ? { data: dom.getAttribute('data-value') }
                        : {};
                }
            }
        ],
        toDOM: (pmNode) => {
            const attrs: Record<string, string> = {
                'data-directive': plugin.name,
                contenteditable: 'false'
            };
            if (plugin.hasData && pmNode.attrs.data) {
                attrs['data-value'] = String(pmNode.attrs.data);
            }
            return ['div', attrs];
        },
        parseMarkdown: {
            match: (mdNode) => {
                const d = mdNode as unknown as DirectiveMdastNode;
                return d.type === 'leafDirective' && d.name === plugin.name;
            },
            runner: (state, mdNode, type) => {
                const d = mdNode as unknown as DirectiveMdastNode;
                state.addNode(type, plugin.hasData ? { data: d.attributes?.data } : {});
            }
        },
        toMarkdown: {
            match: (pmNode) => pmNode.type.name === plugin.name,
            runner: (state, pmNode) => {
                const attributes: Record<string, string> = {};
                if (plugin.hasData && pmNode.attrs.data) {
                    attributes.data = String(pmNode.attrs.data);
                }
                state.addNode('leafDirective', undefined, undefined, {
                    name: plugin.name,
                    ...(Object.keys(attributes).length > 0
                        ? { attributes }
                        : {})
                });
            }
        }
    }));

    const viewPlugin = $view(nodePlugin, () =>
        nodeViewFactory({
            component: () => React.createElement(DirectiveNodeView, { plugin }),
            as: 'div',
            // Prevent ProseMirror from capturing events that should be handled
            // by interactive elements inside block views (textareas, buttons,
            // drag-to-pan containers, etc.).
            stopEvent: () => true
        })
    );

    return [nodePlugin, viewPlugin];
}
